import { useState, useEffect, useCallback } from "react";
import {
  FileEdit,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Save,
  X,
  Code,
  Type,
  Variable,
  Copy,
  Check,
  ArrowLeft,
  Info,
  Zap,
  ToggleLeft,
  ToggleRight,
  Settings,
  Play,
  Loader2,
  Send,
} from "lucide-react";
import api from "@/lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";

// ─── Interfaces ─────────────────────────────────────────────────────────────

interface EmailTemplate {
  id: string;
  name: string;
  type: string;
  subject: string;
  htmlContent: string;
  variables: string[] | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EmailTypeVariable {
  name: string;
  description: string;
}

interface EmailType {
  type: string;
  label: string;
  description: string;
  variables: EmailTypeVariable[];
  hasTemplate: boolean;
}

interface PreviewData {
  subject: string;
  html: string;
}

interface EmailAutomation {
  id: string;
  type: string;
  name: string;
  description: string | null;
  isEnabled: boolean;
  triggerType: string;
  triggerConfig: Record<string, unknown>;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function AdminEmailTemplates() {
  // State
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [emailTypes, setEmailTypes] = useState<EmailType[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"list" | "editor">("list");

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null,
  );
  const [isPersonalizing, setIsPersonalizing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    subject: "",
    htmlContent: "",
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const { showToast } = useGlobalToast();

  // Preview state
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Variable help
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  // Content editor tab
  const [contentTab, setContentTab] = useState<"visual" | "html">("visual");

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Automations state
  const [automations, setAutomations] = useState<EmailAutomation[]>([]);
  const [editingAutomation, setEditingAutomation] = useState<string | null>(
    null,
  );
  const [automationSaving, setAutomationSaving] = useState(false);
  const [executingAutomation, setExecutingAutomation] = useState<string | null>(
    null,
  );
  const [executeResult, setExecuteResult] = useState<{
    id: string;
    message: string;
  } | null>(null);

  // Execute modal state
  const [executeModal, setExecuteModal] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [executeFilters, setExecuteFilters] = useState({
    minDaysInactive: 5,
    maxDaysInactive: 0,
    planFilter: "all" as "all" | "free" | "paid",
    testEmail: "",
  });
  const [executePreview, setExecutePreview] = useState<{
    totalUsers: number;
    preview: Array<{
      email: string;
      firstName: string;
      restaurantName: string;
      planName: string;
      daysSinceLastLogin: number | null;
    }>;
  } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ─── Data fetching ──────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get("/admin/email-templates");
      setTemplates(res.data.data);
    } catch (err) {
      console.error("Error fetching templates:", err);
    }
  }, []);

  const fetchEmailTypes = useCallback(async () => {
    try {
      const res = await api.get("/admin/email-templates/types");
      setEmailTypes(res.data.data);
    } catch (err) {
      console.error("Error fetching email types:", err);
    }
  }, []);

  const fetchAutomations = useCallback(async () => {
    try {
      const res = await api.get("/admin/email-automations");
      let data = res.data.data || [];

      // Siempre ejecutar seed para crear automatizaciones nuevas que falten
      const seedRes = await api.post("/admin/email-automations/seed");
      if (seedRes.data.data?.created > 0) {
        const res2 = await api.get("/admin/email-automations");
        data = res2.data.data || [];
      }

      setAutomations(data);
    } catch (err) {
      console.error("Error fetching automations:", err);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      fetchTemplates(),
      fetchEmailTypes(),
      fetchAutomations(),
    ]).finally(() => setLoading(false));
  }, [fetchTemplates, fetchEmailTypes, fetchAutomations]);

  // ─── Helpers ────────────────────────────────────────────────────────────

  const getTypeLabel = (type: string): string => {
    const found = emailTypes.find((t) => t.type === type);
    return found?.label || type;
  };

  const getTypeVariables = (type: string): EmailTypeVariable[] => {
    const found = emailTypes.find((t) => t.type === type);
    return found?.variables || [];
  };

  const getTypeDescription = (type: string): string => {
    const found = emailTypes.find((t) => t.type === type);
    return found?.description || "";
  };

  const availableTypes = emailTypes.filter(
    (t) =>
      !t.hasTemplate || (editingTemplate && editingTemplate.type === t.type),
  );

  // ─── Editor actions ─────────────────────────────────────────────────────

  const openCreateEditor = () => {
    setEditingTemplate(null);
    setIsPersonalizing(false);
    setFormData({
      name: "",
      type: "",
      subject: "",
      htmlContent: getDefaultHtmlTemplate(),
      isActive: true,
    });
    setPreview(null);
    setShowPreview(false);
    setView("editor");
  };

  const openEditEditor = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsPersonalizing(false);
    setFormData({
      name: template.name,
      type: template.type,
      subject: template.subject,
      htmlContent: template.htmlContent,
      isActive: template.isActive,
    });
    setPreview(null);
    setShowPreview(false);
    setView("editor");
  };

  const openPersonalizeEditor = async (emailType: EmailType) => {
    setEditingTemplate(null);
    setIsPersonalizing(true);

    // Intentar cargar la plantilla por defecto real del backend
    try {
      const res = await api.get(
        `/admin/email-templates/default/${emailType.type}`,
      );
      const defaultTpl = res.data.data;
      setFormData({
        name: emailType.label,
        type: emailType.type,
        subject: defaultTpl.subject,
        htmlContent: defaultTpl.html,
        isActive: true,
      });
    } catch {
      // Si falla, usar plantilla genérica
      setFormData({
        name: emailType.label,
        type: emailType.type,
        subject: "",
        htmlContent: getDefaultHtmlTemplate(),
        isActive: true,
      });
    }

    setPreview(null);
    setShowPreview(false);
    setView("editor");
  };

  const handleSave = async () => {
    if (
      !formData.name.trim() ||
      !formData.type ||
      !formData.subject.trim() ||
      !formData.htmlContent.trim()
    ) {
      showToast("Todos los campos son obligatorios", "error");
      return;
    }

    setSaving(true);

    try {
      const variables = getTypeVariables(formData.type).map((v) => v.name);
      const payload = {
        ...formData,
        variables,
      };

      if (editingTemplate) {
        await api.put(`/admin/email-templates/${editingTemplate.id}`, payload);
        showToast("Plantilla actualizada correctamente", "success");
      } else {
        await api.post("/admin/email-templates", payload);
        showToast("Plantilla creada correctamente", "success");
      }

      await Promise.all([fetchTemplates(), fetchEmailTypes()]);
      setView("list");
    } catch (err: any) {
      showToast(
        err.response?.data?.message || "Error al guardar la plantilla",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/admin/email-templates/${id}`);
      await Promise.all([fetchTemplates(), fetchEmailTypes()]);
      setDeleteConfirm(null);
    } catch (err: any) {
      console.error("Error deleting template:", err);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/admin/email-templates/preview", {
        subject: formData.subject,
        htmlContent: formData.htmlContent,
      });
      setPreview(res.data.data);
      setShowPreview(true);
    } catch (err: any) {
      showToast(
        err.response?.data?.message || "Error al generar la vista previa",
        "error",
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const copyVariable = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  // ─── Automation handlers ──────────────────────────────────────────────

  const handleToggleAutomation = async (id: string, isEnabled: boolean) => {
    setAutomationSaving(true);
    try {
      await api.put(`/admin/email-automations/${id}`, { isEnabled });
      setAutomations((prev) =>
        prev.map((a) => (a.id === id ? { ...a, isEnabled } : a)),
      );
    } catch (err) {
      console.error("Error toggling automation:", err);
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleUpdateAutomationConfig = async (
    id: string,
    triggerConfig: Record<string, unknown>,
  ) => {
    setAutomationSaving(true);
    try {
      await api.put(`/admin/email-automations/${id}`, { triggerConfig });
      setAutomations((prev) =>
        prev.map((a) => (a.id === id ? { ...a, triggerConfig } : a)),
      );
      setEditingAutomation(null);
    } catch (err) {
      console.error("Error updating automation config:", err);
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleExecuteAutomation = async (id: string) => {
    setExecutingAutomation(id);
    setExecuteResult(null);
    try {
      const body: Record<string, unknown> = {
        minDaysInactive: executeFilters.minDaysInactive,
        planFilter: executeFilters.planFilter,
      };
      if (executeFilters.maxDaysInactive > 0) {
        body.maxDaysInactive = executeFilters.maxDaysInactive;
      }
      if (executeFilters.testEmail.trim()) {
        body.testEmail = executeFilters.testEmail.trim();
      }
      const res = await api.post(
        `/admin/email-automations/${id}/execute`,
        body,
      );
      setExecuteResult({ id, message: res.data.message });
      setExecuteModal(null);
    } catch (err: any) {
      setExecuteResult({
        id,
        message: err.response?.data?.message || "Error al ejecutar",
      });
    } finally {
      setExecutingAutomation(null);
    }
  };

  const handlePreviewAudience = async (id: string) => {
    setLoadingPreview(true);
    setExecutePreview(null);
    try {
      const body: Record<string, unknown> = {
        minDaysInactive: executeFilters.minDaysInactive,
        planFilter: executeFilters.planFilter,
      };
      if (executeFilters.maxDaysInactive > 0) {
        body.maxDaysInactive = executeFilters.maxDaysInactive;
      }
      const res = await api.post(
        `/admin/email-automations/${id}/preview`,
        body,
      );
      setExecutePreview(res.data.data);
    } catch (err: any) {
      console.error("Error previewing audience:", err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const openExecuteModal = (auto: EmailAutomation) => {
    const config = (auto.triggerConfig || {}) as Record<string, any>;
    setExecuteFilters({
      minDaysInactive: (config.minDaysInactive as number) || 5,
      maxDaysInactive: 0,
      planFilter: "all",
      testEmail: "",
    });
    setExecutePreview(null);
    setExecuteResult(null);
    setExecuteModal({ id: auto.id, name: auto.name });
  };

  // ─── Default template ───────────────────────────────────────────────────

  const getDefaultHtmlTemplate = () => `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
      
      <!-- Header -->
      <div style="background: linear-gradient(135deg, #4f46e5, #7c3aed); padding: 30px; text-align: center;">
        <h1 style="margin: 0; color: #fff; font-size: 22px;">📅 ReservaManager</h1>
        <p style="margin: 8px 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">Tu asunto aquí</p>
      </div>
      
      <!-- Content -->
      <div style="padding: 30px;">
        <p style="color: #374151; font-size: 16px; line-height: 1.6;">
          Hola <strong>{{firstName}}</strong>,
        </p>
        <p style="color: #374151; font-size: 16px; line-height: 1.6;">
          Escribe aquí el contenido de tu email.
        </p>
      </div>
      
      <!-- Footer -->
      <div style="background: #f9fafb; padding: 16px; text-align: center; border-top: 1px solid #eee;">
        <p style="margin: 0; color: #999; font-size: 12px;">© ${new Date().getFullYear()} ReservaManager</p>
      </div>
      
    </div>
  </div>
</body>
</html>`;

  // ─── Loading ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Editor View ────────────────────────────────────────────────────────

  if (view === "editor") {
    const currentVariables = formData.type
      ? getTypeVariables(formData.type)
      : [];

    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 md:mb-6 flex items-center gap-3">
            <button
              onClick={() => setView("list")}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {editingTemplate
                  ? "Editar Plantilla"
                  : isPersonalizing
                    ? "Personalizar Plantilla"
                    : "Nueva Plantilla"}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {editingTemplate
                  ? `Editando: ${editingTemplate.name}`
                  : isPersonalizing
                    ? `Personalizando: ${formData.name}`
                    : "Crea una plantilla de email personalizada"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreview}
                disabled={previewLoading || !formData.htmlContent.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vista previa</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 text-sm"
              >
                <Save className="w-4 h-4" />
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            {/* Editor panel */}
            <div className="flex-1 space-y-4">
              {/* Name & Type */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de la plantilla
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Ej: Confirmación de reserva personalizada"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de email
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="">Seleccionar tipo...</option>
                      {availableTypes.map((t) => (
                        <option key={t.type} value={t.type}>
                          {t.label}
                          {t.hasTemplate && editingTemplate?.type !== t.type
                            ? " (ya tiene plantilla)"
                            : ""}
                        </option>
                      ))}
                    </select>
                    {formData.type && (
                      <p className="mt-1 text-xs text-gray-500">
                        {getTypeDescription(formData.type)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Active toggle */}
                <div className="mt-4 flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">
                    {formData.isActive
                      ? "Activa"
                      : "Inactiva (se usará la plantilla por defecto)"}
                  </span>
                </div>

                {/* Variables disponibles - inline */}
                {currentVariables.length > 0 && (
                  <div className="mt-4 border border-primary-200 bg-primary-50/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Variable className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-primary-800">
                        Variables disponibles para este tipo
                      </span>
                    </div>
                    <p className="text-xs text-primary-600/80 mb-2.5">
                      Usa estas variables en el asunto o HTML. Se reemplazarán
                      automáticamente con los datos reales al enviar.
                    </p>
                    <div className="space-y-1">
                      {currentVariables.map((v) => (
                        <div
                          key={v.name}
                          className="flex items-center gap-2 group"
                        >
                          <button
                            onClick={() => copyVariable(v.name)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-primary-200 hover:border-primary-400 hover:bg-primary-50 rounded-md text-xs font-mono text-primary-700 transition-colors flex-shrink-0"
                            title={`Copiar {{${v.name}}}`}
                          >
                            {copiedVar === v.name ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 opacity-40" />
                            )}
                            {`{{${v.name}}}`}
                          </button>
                          <span className="text-xs text-primary-600/70 truncate">
                            {v.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {!formData.type && (
                  <div className="mt-4 border border-gray-200 bg-gray-50 rounded-lg p-3 text-center">
                    <Variable className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">
                      Selecciona un tipo de email para ver las variables
                      disponibles
                    </p>
                  </div>
                )}
              </div>

              {/* Subject */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto del email
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData({ ...formData, subject: e.target.value })
                  }
                  placeholder="Ej: ✓ Tu reserva en {{restaurantName}} está confirmada"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Puedes usar variables como {"{{variable}}"} en el asunto
                </p>
              </div>

              {/* HTML Content - Tabbed editor */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setContentTab("visual")}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                      contentTab === "visual"
                        ? "border-primary-600 text-primary-700 bg-primary-50/50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Visual
                  </button>
                  <button
                    onClick={() => setContentTab("html")}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                      contentTab === "html"
                        ? "border-primary-600 text-primary-700 bg-primary-50/50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    Código HTML
                  </button>
                </div>

                <div className="p-4 md:p-6">
                  {contentTab === "visual" ? (
                    /* Visual preview - rendered HTML */
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        Vista del email renderizado. Edita en la pestaña "Código
                        HTML".
                      </p>
                      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                        {formData.htmlContent.trim() ? (
                          <iframe
                            srcDoc={formData.htmlContent}
                            title="Vista visual del email"
                            className="w-full border-0"
                            style={{ height: "520px" }}
                            sandbox="allow-same-origin"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                            <div className="text-center">
                              <Type className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>
                                Escribe HTML en la pestaña "Código HTML" para
                                ver la vista previa aquí
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* HTML code editor */
                    <div>
                      <p className="text-xs text-gray-500 mb-2">
                        Edita directamente el HTML del email. Puedes copiar y
                        pegar desde otro editor.
                      </p>
                      <textarea
                        id="htmlContent"
                        value={formData.htmlContent}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            htmlContent: e.target.value,
                          })
                        }
                        rows={20}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                        placeholder="<!DOCTYPE html>..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right sidebar: Preview + Help */}
            <div className="lg:w-80 space-y-4">
              {/* Preview panel */}
              {showPreview && preview && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-primary-600" />
                      <span className="text-sm font-medium text-gray-700">
                        Vista previa
                      </span>
                    </div>
                    <button
                      onClick={() => setShowPreview(false)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-gray-500 mb-1">Asunto:</p>
                    <p className="text-sm font-medium text-gray-900 mb-3">
                      {preview.subject}
                    </p>
                    <div
                      className="border border-gray-200 rounded-lg overflow-hidden"
                      style={{ maxHeight: "500px", overflowY: "auto" }}
                    >
                      <iframe
                        srcDoc={preview.html}
                        title="Vista previa del email"
                        className="w-full border-0"
                        style={{ height: "450px" }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Quick help */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">
                  💡 Consejos
                </h4>
                <ul className="text-xs text-blue-700 space-y-1.5">
                  <li>
                    • Usa{" "}
                    <code className="bg-blue-100 px-1 rounded">
                      {"{{variable}}"}
                    </code>{" "}
                    para insertar datos dinámicos
                  </li>
                  <li>
                    • El HTML debe usar estilos inline para compatibilidad con
                    clientes de email
                  </li>
                  <li>
                    • Si desactivas la plantilla, se usará la plantilla por
                    defecto del sistema
                  </li>
                  <li>
                    • Al eliminar una plantilla, el email vuelve a usar el
                    diseño original
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              <FileEdit className="w-6 h-6 inline-block mr-2 text-primary-600" />
              Plantillas de Email
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Personaliza las plantillas de los emails enviados por la
              plataforma
            </p>
          </div>
          <button
            onClick={openCreateEditor}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            <Plus className="w-4 h-4" />
            Nueva Plantilla
          </button>
        </div>

        {/* Existing templates */}
        {templates.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                Plantillas personalizadas ({templates.length})
              </h3>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {templates.map((tpl) => (
                <div key={tpl.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {tpl.name}
                      </p>
                      <p className="text-sm text-primary-600">
                        {getTypeLabel(tpl.type)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 truncate">
                        {tpl.subject}
                      </p>
                      <span
                        className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                          tpl.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {tpl.isActive ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEditEditor(tpl)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      {deleteConfirm === tpl.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(tpl.id)}
                            className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                          >
                            <Check className="w-4 h-4 text-red-600" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(tpl.id)}
                          className="p-2 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Asunto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {templates.map((tpl) => (
                    <tr
                      key={tpl.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {tpl.name}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-block text-xs px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full font-medium">
                          {getTypeLabel(tpl.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600 truncate max-w-xs">
                          {tpl.subject}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block text-xs px-2.5 py-1 rounded-full font-medium ${
                            tpl.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {tpl.isActive ? "Activa" : "Inactiva"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditEditor(tpl)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                          {deleteConfirm === tpl.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDelete(tpl.id)}
                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg"
                                title="Confirmar eliminación"
                              >
                                <Check className="w-4 h-4 text-red-600" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                                title="Cancelar"
                              >
                                <X className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(tpl.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info banner */}
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              ¿Cómo se configuran los emails automáticos?
            </p>
            <p className="text-blue-700">
              Todos los emails que la plataforma envía automáticamente aparecen
              en la lista de abajo. Los que tienen etiqueta{" "}
              <span className="font-medium">"Por defecto"</span> usan la
              plantilla del sistema. Haz clic en{" "}
              <span className="font-medium">"Personalizar"</span> para crear tu
              propia versión con tu diseño y textos. Si ya tienes una plantilla
              personalizada, puedes{" "}
              <span className="font-medium">"Editar"</span> o desactivarla para
              volver al diseño por defecto.
            </p>
          </div>
        </div>

        {/* Automations section */}
        {automations.length > 0 && (
          <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <h3 className="text-base font-semibold text-gray-900">
                  Automatizaciones ({automations.length})
                </h3>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">
                Activa o desactiva emails automáticos y configura cuándo se
                envían.
              </p>
            </div>

            <div className="divide-y divide-gray-100">
              {automations.map((auto) => {
                const config = (auto.triggerConfig || {}) as Record<
                  string,
                  any
                >;
                const isEditing = editingAutomation === auto.id;

                return (
                  <div key={auto.id} className="p-4 md:px-6 md:py-4">
                    <div className="flex items-start sm:items-center gap-3">
                      {/* Toggle */}
                      <button
                        onClick={() =>
                          handleToggleAutomation(auto.id, !auto.isEnabled)
                        }
                        disabled={automationSaving}
                        className="flex-shrink-0 mt-0.5 sm:mt-0"
                        title={auto.isEnabled ? "Desactivar" : "Activar"}
                      >
                        {auto.isEnabled ? (
                          <ToggleRight className="w-8 h-8 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-gray-300" />
                        )}
                      </button>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p
                            className={`text-sm font-medium ${auto.isEnabled ? "text-gray-900" : "text-gray-400"}`}
                          >
                            {auto.name}
                          </p>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${auto.isEnabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}
                          >
                            {auto.isEnabled ? "Activo" : "Inactivo"}
                          </span>
                        </div>
                        <p
                          className={`text-xs mt-0.5 ${auto.isEnabled ? "text-gray-500" : "text-gray-400"}`}
                        >
                          {auto.description}
                        </p>

                        {/* Current config summary */}
                        {auto.isEnabled && !isEditing && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {auto.triggerType === "days_before_trial_end" && (
                              <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full">
                                {config.daysBefore || 3} días antes del fin de
                                prueba
                              </span>
                            )}
                            {auto.triggerType === "days_after_registration" && (
                              <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                                {config.daysAfter || 2} días después del
                                registro
                              </span>
                            )}
                            {auto.triggerType === "recurring_free" && (
                              <>
                                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                                  Desde día {config.startDay || 15}
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                                  Cada {config.repeatEveryDays || 15} días
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full">
                                  Hasta día {config.maxDay || 90}
                                </span>
                              </>
                            )}
                            {auto.triggerType === "days_inactive" && (
                              <span className="text-xs px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full">
                                Después de{" "}
                                {(config.inactiveDays || [14, 30]).join(", ")}{" "}
                                días sin actividad
                              </span>
                            )}
                            {auto.triggerType === "on_event" && (
                              <span className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full">
                                Se envía automáticamente al ocurrir el evento
                              </span>
                            )}
                            {auto.triggerType === "manual" && (
                              <span className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">
                                Ejecución manual — usuarios +
                                {(config.minDaysInactive as number) || 5} días
                                inactivos
                              </span>
                            )}
                          </div>
                        )}

                        {/* Inline editor */}
                        {isEditing && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-3">
                            {auto.triggerType === "days_before_trial_end" && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Días antes del fin de prueba
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={14}
                                  defaultValue={config.daysBefore || 3}
                                  className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (val >= 1 && val <= 14) {
                                      handleUpdateAutomationConfig(auto.id, {
                                        ...config,
                                        daysBefore: val,
                                      });
                                    }
                                  }}
                                />
                              </div>
                            )}
                            {auto.triggerType === "days_after_registration" && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Días después del registro
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={30}
                                  defaultValue={config.daysAfter || 2}
                                  className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (val >= 1 && val <= 30) {
                                      handleUpdateAutomationConfig(auto.id, {
                                        ...config,
                                        daysAfter: val,
                                      });
                                    }
                                  }}
                                />
                              </div>
                            )}
                            {auto.triggerType === "recurring_free" && (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Empezar desde día
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    defaultValue={config.startDay || 15}
                                    id={`startDay-${auto.id}`}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Repetir cada (días)
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    defaultValue={config.repeatEveryDays || 15}
                                    id={`repeatEveryDays-${auto.id}`}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Hasta día
                                  </label>
                                  <input
                                    type="number"
                                    min={1}
                                    max={365}
                                    defaultValue={config.maxDay || 90}
                                    id={`maxDay-${auto.id}`}
                                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </div>
                                <div className="sm:col-span-3">
                                  <button
                                    onClick={() => {
                                      const startDay = parseInt(
                                        (
                                          document.getElementById(
                                            `startDay-${auto.id}`,
                                          ) as HTMLInputElement
                                        )?.value || "15",
                                      );
                                      const repeatEveryDays = parseInt(
                                        (
                                          document.getElementById(
                                            `repeatEveryDays-${auto.id}`,
                                          ) as HTMLInputElement
                                        )?.value || "15",
                                      );
                                      const maxDay = parseInt(
                                        (
                                          document.getElementById(
                                            `maxDay-${auto.id}`,
                                          ) as HTMLInputElement
                                        )?.value || "90",
                                      );
                                      handleUpdateAutomationConfig(auto.id, {
                                        ...config,
                                        startDay,
                                        repeatEveryDays,
                                        maxDay,
                                      });
                                    }}
                                    disabled={automationSaving}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                                  >
                                    <Save className="w-3.5 h-3.5" />
                                    Guardar
                                  </button>
                                </div>
                              </div>
                            )}
                            {auto.triggerType === "days_inactive" && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Días sin actividad (separados por coma)
                                </label>
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    defaultValue={(
                                      config.inactiveDays || [14, 30]
                                    ).join(", ")}
                                    placeholder="14, 30, 60"
                                    className="w-48 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                    onBlur={(e) => {
                                      const days = e.target.value
                                        .split(",")
                                        .map((d) => parseInt(d.trim()))
                                        .filter((d) => !isNaN(d) && d > 0);
                                      if (days.length > 0) {
                                        handleUpdateAutomationConfig(auto.id, {
                                          ...config,
                                          inactiveDays: days,
                                        });
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                            {auto.triggerType === "on_event" && (
                              <p className="text-xs text-gray-500">
                                Este email se envía automáticamente cuando
                                ocurre el evento. No hay configuración de tiempo
                                adicional.
                              </p>
                            )}
                            {auto.triggerType === "manual" && (
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Mínimo de días inactivo para recibir el email
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={365}
                                  defaultValue={
                                    (config.minDaysInactive as number) || 5
                                  }
                                  className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (val >= 1 && val <= 365) {
                                      handleUpdateAutomationConfig(auto.id, {
                                        ...config,
                                        minDaysInactive: val,
                                      });
                                    }
                                  }}
                                />
                              </div>
                            )}
                            <button
                              onClick={() => setEditingAutomation(null)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cerrar
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Edit config button */}
                      {auto.isEnabled && auto.triggerType !== "on_event" && (
                        <button
                          onClick={() =>
                            setEditingAutomation(isEditing ? null : auto.id)
                          }
                          className="flex-shrink-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Configurar timing"
                        >
                          <Settings className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}

                      {/* Execute now button (for all automations) */}
                      {auto.isEnabled && (
                        <button
                          onClick={() => openExecuteModal(auto)}
                          className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                          title="Configurar y enviar a usuarios que cumplan los criterios"
                        >
                          <Play className="w-3.5 h-3.5" />
                          Ejecutar ahora
                        </button>
                      )}
                    </div>

                    {/* Execute result message */}
                    {executeResult?.id === auto.id && (
                      <div
                        className={`mt-2 p-2 rounded-lg text-xs ${
                          executeResult.message.includes("Error")
                            ? "bg-red-50 text-red-700"
                            : "bg-green-50 text-green-700"
                        }`}
                      >
                        {executeResult.message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All email types */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200">
            <h3 className="text-base font-semibold text-gray-900">
              Tipos de email ({emailTypes.length})
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Todos los tipos de email que envía la plataforma. Crea una
              plantilla para personalizar cualquiera.
            </p>
          </div>

          <div className="divide-y divide-gray-100">
            {emailTypes.map((et) => {
              const existingTemplate = templates.find(
                (t) => t.type === et.type,
              );

              return (
                <div
                  key={et.type}
                  className="p-4 md:px-6 md:py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">
                        {et.label}
                      </p>
                      {existingTemplate ? (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            existingTemplate.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {existingTemplate.isActive
                            ? "Personalizada"
                            : "Personalizada (inactiva)"}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          Por defecto
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {et.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {et.variables.slice(0, 5).map((v) => (
                        <span
                          key={v.name}
                          className="text-xs px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded font-mono"
                          title={v.description}
                        >
                          {v.name}
                        </span>
                      ))}
                      {et.variables.length > 5 && (
                        <span className="text-xs text-gray-400">
                          +{et.variables.length - 5} más
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {existingTemplate ? (
                      <button
                        onClick={() => openEditEditor(existingTemplate)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Editar
                      </button>
                    ) : (
                      <button
                        onClick={() => openPersonalizeEditor(et)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Personalizar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Execute Automation Modal ───────────────────────────────────────── */}
      {executeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setExecuteModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Play className="w-5 h-5 text-primary-600" />
                  Ejecutar envío
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {executeModal.name}
                </p>
              </div>
              <button
                onClick={() => setExecuteModal(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Filters */}
            <div className="p-6 space-y-5">
              {/* Days inactive range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Días de inactividad
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Mínimo
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={
                        executeFilters.minDaysInactive === 0
                          ? ""
                          : executeFilters.minDaysInactive
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setExecuteFilters({
                            ...executeFilters,
                            minDaysInactive: 0,
                          });
                        } else {
                          const val = parseInt(raw);
                          if (!isNaN(val))
                            setExecuteFilters({
                              ...executeFilters,
                              minDaysInactive: val,
                            });
                        }
                      }}
                      onBlur={() => {
                        if (executeFilters.minDaysInactive < 1)
                          setExecuteFilters({
                            ...executeFilters,
                            minDaysInactive: 1,
                          });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Máximo (0 = sin límite)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={365}
                      value={
                        executeFilters.maxDaysInactive === 0
                          ? ""
                          : executeFilters.maxDaysInactive
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setExecuteFilters({
                            ...executeFilters,
                            maxDaysInactive: 0,
                          });
                        } else {
                          const val = parseInt(raw);
                          if (!isNaN(val))
                            setExecuteFilters({
                              ...executeFilters,
                              maxDaysInactive: val,
                            });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {executeFilters.maxDaysInactive > 0
                    ? `Usuarios inactivos entre ${executeFilters.minDaysInactive} y ${executeFilters.maxDaysInactive} días`
                    : `Usuarios inactivos más de ${executeFilters.minDaysInactive} días`}
                </p>
              </div>

              {/* Plan filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filtrar por tipo de plan
                </label>
                <div className="flex gap-2">
                  {[
                    { value: "all" as const, label: "Todos" },
                    { value: "free" as const, label: "Gratuitos" },
                    { value: "paid" as const, label: "De pago" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() =>
                        setExecuteFilters({
                          ...executeFilters,
                          planFilter: opt.value,
                        })
                      }
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        executeFilters.planFilter === opt.value
                          ? "bg-primary-100 text-primary-700 border border-primary-300"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview button */}
              <div>
                <button
                  onClick={() => handlePreviewAudience(executeModal.id)}
                  disabled={loadingPreview}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {loadingPreview ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  {loadingPreview ? "Calculando..." : "Ver destinatarios"}
                </button>
              </div>

              {/* Preview results */}
              {executePreview && (
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900">
                      {executePreview.totalUsers} usuario
                      {executePreview.totalUsers !== 1 ? "s" : ""} encontrado
                      {executePreview.totalUsers !== 1 ? "s" : ""}
                    </p>
                  </div>
                  {executePreview.preview.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                      {executePreview.preview.map((u, i) => (
                        <div
                          key={i}
                          className="px-4 py-2 flex items-center justify-between gap-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-900 truncate">
                              {u.firstName} — {u.restaurantName}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {u.email}
                            </p>
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                            <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                              {u.planName}
                            </span>
                            <span className="text-xs text-gray-400">
                              {u.daysSinceLastLogin != null
                                ? `${u.daysSinceLastLogin}d inactivo`
                                : "Sin login"}
                            </span>
                          </div>
                        </div>
                      ))}
                      {executePreview.totalUsers > 20 && (
                        <div className="px-4 py-2 text-xs text-gray-400 text-center">
                          ...y {executePreview.totalUsers - 20} más
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center text-gray-400 text-sm">
                      No hay usuarios que cumplan estos criterios
                    </div>
                  )}
                </div>
              )}

              {/* Test email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de prueba{" "}
                  <span className="font-normal text-gray-400">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={executeFilters.testEmail}
                  onChange={(e) =>
                    setExecuteFilters({
                      ...executeFilters,
                      testEmail: e.target.value,
                    })
                  }
                  placeholder="tu@email.com — Envía solo una prueba a esta dirección"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Si escribes un email, se enviará{" "}
                  <strong>solo una copia de prueba</strong> a esa dirección (con
                  datos del primer usuario).
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
              <button
                onClick={() => setExecuteModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleExecuteAutomation(executeModal.id)}
                disabled={executingAutomation === executeModal.id}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {executingAutomation === executeModal.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {executeFilters.testEmail.trim()
                  ? "Enviar prueba"
                  : `Enviar a ${executePreview?.totalUsers ?? "?"} usuarios`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
